const _require = require('@truqc/content-path'),
  keysSortedByValueAscending = _require.keysSortedByValueAscending;
const _require2 = require(/* webpackIgnore: true */'../../lib/evaluateSource'),
  evaluateSource = _require2.evaluateSource;

/**
 * @function cloneDeep - Deep clone a JavaScript object/collection
 * @param {Object} obj
 * @returns {Object}
 */
const cloneDeep = obj => JSON.parse(JSON.stringify(obj));

/**
 * @function calculateTmin - Calculate the minimum required thickness for the given parameters.
 * @param {number} totalHeight - Cumulative course height so far
 * @param {number} diameter
 * @param {number} specificGravity
 * @param {number} allowableStress
 * @param {number} jointEfficiency
 * @returns {number|undefined}
 */
const calculateTmin = (totalHeight, diameter, specificGravity, allowableStress, jointEfficiency) => {
  if ([totalHeight, diameter, specificGravity, allowableStress, jointEfficiency].some(v => v === undefined)) {
    return undefined;
  }
  return 2.6 * (totalHeight - 1) * diameter * specificGravity / (allowableStress * jointEfficiency);
};

/**
 * @function formatForUI - Format a numeric value for UI precision without
 *    altering original calculation value
 * @param {number} value
 * @param {number} precision
 * @returns {number|undefined}
 */
const formatForUI = (value, precision) => {
  if (value === undefined || Number.isNaN(Number(value))) {
    return undefined;
  }
  return Number(value.toFixed(precision));
};

/**
 * @function calculateCorrosionRate - Compute corrosion rate based on build year
 *     and current thickness
 * @param {string|Date} builtDate - Original manufacture date
 * @param {string|Date} inspectDate - Inspection date or year
 * @param {number} originalThickness
 * @param {number} currentThickness
 * @returns {number|undefined}
 */
const calculateCorrosionRate = (builtDate, inspectDate, originalThickness, currentThickness) => {
  if ([builtDate, inspectDate, originalThickness, currentThickness].some(v => v === undefined)) {
    return undefined;
  }
  const yearsInService = new Date(inspectDate).getFullYear() - new Date(builtDate).getFullYear();
  if (yearsInService <= 0) {
    return undefined;
  }
  return (originalThickness - currentThickness) / yearsInService;
};

/**
 * @function calculateRemainingLife - Determine remaining life based on
 *    thickness allowances and corrosion rate
 * @param {number} remainingAllowance
 * @param {number} corrosionRate
 * @param {boolean} isOverOriginal
 * @param {boolean} isUnderMinRequired
 * @returns {number|undefined}
 */
const calculateRemainingLife = (remainingAllowance, corrosionRate, isOverOriginal, isUnderMinRequired) => {
  if (isOverOriginal) {
    return Number(1000); // default large value when above original thickness
  }
  if (isUnderMinRequired) {
    return Number(0); // immediate failure
  }
  if ([remainingAllowance, corrosionRate].every(v => v !== undefined && !Number.isNaN(v))) {
    return remainingAllowance / corrosionRate;
  }
  return undefined;
};

/**
 * @function determineNextInspections - Schedule next inspections (UT and EX)
 *    based on corrosion data
 * @param {boolean} isUnderMinRequired
 * @param {boolean} isOverOriginal
 * @param {number} remainingAllowance
 * @param {number} corrosionRate
 * @returns {{ nextUt: number|undefined, nextEx: number|undefined }}
 */
const determineNextInspections = (isUnderMinRequired, isOverOriginal, remainingAllowance, corrosionRate) => {
  let nextUt;
  let nextEx;
  if (isUnderMinRequired && isOverOriginal) {
    // Already within safe range
    nextUt = 0;
    nextEx = 0;
  } else if (!isUnderMinRequired && isOverOriginal) {
    // Healthy range, schedule standard intervals
    nextUt = 15;
    nextEx = 5;
  } else if (!isUnderMinRequired && !isOverOriginal && remainingAllowance !== undefined && corrosionRate !== undefined) {
    // Calculate based on half and quarter of remaining allowance
    nextUt = Math.min(15, Math.abs(remainingAllowance / (2 * corrosionRate)));
    nextEx = Math.min(5, Math.abs(remainingAllowance / (4 * corrosionRate)));
  }
  return {
    nextUt: formatForUI(nextUt, 0),
    nextEx: formatForUI(nextEx, 0)
  };
};

/**
 * @function determineResult - Determine pass/fail result based on current vs. required thickness
 * @param {number} current
 * @param {number} required
 * @returns {string|undefined}
 */
const determineResult = (current, required) => {
  if ([current, required].some(v => v === undefined)) {
    return undefined;
  }
  return current > required ? 'PASS' : 'FAIL';
};

/**
 * Main evaluation entry point
 */
const evaluate = async (source, content, parentContentPath, transientContext, nativeFunctions) => {
  // Fetch base collection
  const rawCollection = await evaluateSource(source.value, content, parentContentPath, transientContext, nativeFunctions);
  if (rawCollection === undefined) {
    return undefined;
  }

  // Fetch essential metadata for calculations
  const builtDate = await evaluateSource(source.manufacturedDate, content, parentContentPath, transientContext, nativeFunctions);
  const specificGravity = await evaluateSource(source.specificGravity, content, parentContentPath, transientContext, nativeFunctions);
  const diameter = await evaluateSource(source.diameter, content, parentContentPath, transientContext, nativeFunctions);

  // If any core parameters are missing, skip detailed evaluation
  if ([builtDate, specificGravity, diameter].some(v => v === undefined)) {
    return rawCollection;
  }

  // Prepare for per-item processing
  const sortedKeys = keysSortedByValueAscending(rawCollection);
  const evaluatedCollection = cloneDeep(rawCollection);
  let cumulativeHeight = 0;

  // Process each item in sorted order
  sortedKeys.forEach(key => {
    const item = evaluatedCollection[key];
    const courseNumber = item.courseNumber,
      currentNominalThickness = item.currentNominalThickness,
      originalThickness = item.originalThickness,
      inspectionYear = item.inspectionYear,
      allowableStress = item.allowableStress,
      jointEfficiency = item.jointEfficiency,
      _created = item._created,
      _item$courseHeight = item.courseHeight,
      courseHeight = _item$courseHeight === void 0 ? 0 : _item$courseHeight;

    // Assign default course number if missing
    if (courseNumber === undefined) {
      item.courseNumber = _created + 1;
    }

    // Update cumulative height and calculate required thickness
    cumulativeHeight += courseHeight;
    const tmin = calculateTmin(cumulativeHeight, diameter, specificGravity, allowableStress, jointEfficiency);

    // Store formatted thickness for UI and raw for logic
    item.tminRequiredThickness = formatForUI(tmin, 3);
    item.productHeight = cumulativeHeight;

    // Compute remaining corrosion allowance if possible
    if (currentNominalThickness !== undefined && tmin !== undefined) {
      item.remainingCorrosionAllowance = currentNominalThickness - tmin;
    }
    const isUnderMinRequired = tmin >= currentNominalThickness;
    const isOverOriginal = currentNominalThickness >= originalThickness;

    // Calculate corrosion rate
    const corrosionRate = calculateCorrosionRate(builtDate, inspectionYear || new Date().toISOString(), originalThickness, currentNominalThickness);
    item.corrosionRate = corrosionRate;

    // Determine remaining life and next inspections
    const remainingLifeRaw = calculateRemainingLife(item.remainingCorrosionAllowance, corrosionRate, isOverOriginal, isUnderMinRequired);
    item.remainingLife = formatForUI(remainingLifeRaw, 2);
    const _determineNextInspect = determineNextInspections(isUnderMinRequired, isOverOriginal, item.remainingCorrosionAllowance, corrosionRate),
      nextUt = _determineNextInspect.nextUt,
      nextEx = _determineNextInspect.nextEx;
    item.nextUt = nextUt;
    item.nextEx = nextEx;

    // Ensure inspectionYear is set
    if (inspectionYear === undefined) {
      item.inspectionYear = new Date().toISOString();
    }

    // Final pass/fail determination
    item.result = determineResult(currentNominalThickness, tmin);
  });
  return evaluatedCollection;
};
module.exports = {
  evaluate
};